import { useEffect, useRef, useState, FormEvent } from "react";

function useInView() {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return { ref, inView };
}

export default function Contact() {
  const { ref, inView } = useInView();
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const textarea = form.querySelector("textarea");
    if (textarea && textarea.value.length > 500) {
      alert("El mensaje no puede superar los 500 caracteres.");
      return;
    }
    setStatus("sending");
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      const res = await fetch("https://readdy.ai/api/form/d79kse1rcamcd36dbipg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      if (res.ok) {
        setStatus("success");
        form.reset();
        setCharCount(0);
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <section id="contacto" className="py-20 md:py-32 bg-[#F8F6F0]" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          {/* Left */}
          <div className={`transition-all duration-700 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <span className="text-[#D4AF37] text-sm font-semibold tracking-widest uppercase">Contáctanos</span>
            <h2 className="text-3xl md:text-5xl font-bold text-[#0A1F44] mt-3 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              Da el primer paso<br />
              <span className="text-[#D4AF37]">hacia tu transformación</span>
            </h2>
            <p className="text-gray-600 text-base md:text-lg leading-relaxed mb-8">
              Estamos aquí para guiarte. Escríbenos por WhatsApp o llena el formulario y un asesor te contactará en menos de 24 horas.
            </p>

            {/* WhatsApp CTA */}
            <a
              href="https://wa.me/523325077743?text=Hola,%20quiero%20más%20información%20sobre%20el%20Instituto%20Ashaika"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 bg-[#25D366] text-white rounded-2xl p-5 mb-6 hover:bg-[#1ebe5d] transition-all duration-300 cursor-pointer"
            >
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white/20 flex-shrink-0">
                <i className="ri-whatsapp-line text-2xl"></i>
              </div>
              <div>
                <div className="font-bold text-lg">Escríbenos por WhatsApp</div>
                <div className="text-white/80 text-sm">+52 33 2507 7743 · Respuesta inmediata</div>
              </div>
              <i className="ri-arrow-right-line ml-auto text-xl"></i>
            </a>

            {/* Social */}
            <div className="bg-white rounded-xl p-5 border border-gray-100">
              <div className="text-[#0A1F44] font-semibold mb-4">Síguenos en redes sociales</div>
              <div className="flex items-center gap-3">
                {[
                  { icon: "ri-instagram-line", label: "Instagram", color: "hover:text-pink-500", href: "https://www.instagram.com/alonso_ashaika/" },
                  { icon: "ri-facebook-circle-line", label: "Facebook", color: "hover:text-blue-600", href: "https://www.facebook.com/Ashtair.Ashaika/" },
                  { icon: "ri-youtube-line", label: "YouTube", color: "hover:text-red-500", href: "https://youtube.com/channel/UC-4Sh1ohGBnO-6trq-sw6LA/" },
                  { icon: "ri-tiktok-line", label: "TikTok", color: "hover:text-gray-900", href: "https://www.tiktok.com/@dralonsoashaika" },
                ].map((s) => (
                  <a
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-10 h-10 flex items-center justify-center rounded-full border border-gray-200 text-gray-500 ${s.color} transition-colors duration-300 cursor-pointer`}
                    aria-label={s.label}
                  >
                    <i className={`${s.icon} text-lg`}></i>
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Form */}
          <div className={`transition-all duration-700 delay-200 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="bg-white rounded-2xl p-8 border border-gray-100">
              <h3 className="text-xl font-bold text-[#0A1F44] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                Formulario de Contacto
              </h3>

              {status === "success" ? (
                <div className="text-center py-10">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-100 text-green-600 mx-auto mb-4">
                    <i className="ri-check-line text-3xl"></i>
                  </div>
                  <h4 className="text-xl font-bold text-[#0A1F44] mb-2">¡Mensaje enviado!</h4>
                  <p className="text-gray-500">Te contactaremos en menos de 24 horas. También puedes escribirnos por WhatsApp para una respuesta inmediata.</p>
                  <button
                    onClick={() => setStatus("idle")}
                    className="mt-6 text-[#D4AF37] font-semibold text-sm cursor-pointer hover:underline"
                  >
                    Enviar otro mensaje
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-readdy-form id="contacto-ashaika">
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Nombre *</label>
                        <input
                          type="text"
                          name="nombre"
                          required
                          placeholder="Tu nombre completo"
                          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 focus:outline-none focus:border-[#D4AF37] transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Teléfono</label>
                        <input
                          type="tel"
                          name="telefono"
                          placeholder="+52 33 0000 0000"
                          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 focus:outline-none focus:border-[#D4AF37] transition-colors"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Correo electrónico *</label>
                      <input
                        type="email"
                        name="email"
                        required
                        placeholder="tu@correo.com"
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 focus:outline-none focus:border-[#D4AF37] transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Programa de interés</label>
                      <select
                        name="programa"
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 focus:outline-none focus:border-[#D4AF37] transition-colors bg-white cursor-pointer"
                      >
                        <option value="">Selecciona un programa</option>
                        <option value="Biomagnetismo Médico">Biomagnetismo Médico</option>
                        <option value="Método Yuen">Método Yuen</option>
                        <option value="Hipnosis Terapéutica">Hipnosis Terapéutica</option>
                        <option value="Bioenergética">Bioenergética</option>
                        <option value="Leyes Biológicas">Leyes Biológicas</option>
                        <option value="Desarrollo Personal y Conciencia">Desarrollo Personal y Conciencia</option>
                        <option value="Sesión Terapéutica">Sesión Terapéutica Individual</option>
                        <option value="Otro">Otro</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Mensaje
                        <span className="text-gray-400 font-normal ml-2 text-xs">({charCount}/500)</span>
                      </label>
                      <textarea
                        name="mensaje"
                        rows={4}
                        maxLength={500}
                        onChange={(e) => setCharCount(e.target.value.length)}
                        placeholder="Cuéntanos cómo podemos ayudarte..."
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 focus:outline-none focus:border-[#D4AF37] transition-colors resize-none"
                      ></textarea>
                    </div>

                    {status === "error" && (
                      <p className="text-red-500 text-sm">Hubo un error al enviar. Por favor intenta de nuevo.</p>
                    )}

                    <button
                      type="submit"
                      disabled={status === "sending"}
                      className="w-full bg-[#0A1F44] text-white font-semibold py-3.5 rounded-xl hover:bg-[#D4AF37] hover:text-[#0A1F44] transition-all duration-300 whitespace-nowrap cursor-pointer disabled:opacity-60"
                    >
                      {status === "sending" ? "Enviando..." : "Enviar Mensaje"}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
